import ArrowCircleLeftIcon from "@mui/icons-material/ArrowCircleLeft";
import CopyAllOutlined from "@mui/icons-material/CopyAllOutlined";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import {
  Box,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import * as React from "react";
import {
  CheckboxGroupInput,
  DateInput,
  ImageField,
  Link,
  required,
  SelectInput,
  TextInput,
  Toolbar,
  useRecordContext,
  useRefresh,
} from "react-admin";
import { useFormContext } from "react-hook-form";
import BankListAside from "../bankers/BankListAside";
import sendRequest from "../dataProvider/sendRequest";
import SectionStyled from "../products/groups/SectionStyled";
import CustomTabButton from "../unit/CustomTabButton";
import HeaderLabelGridField from "../unit/HeaderLabelGridField";
import HmSubHeaderTitle from "../unit/HmSubHeaderTitle";
import HmTextInput from "../unit/HmTextInput";
import UploadImage from "../unit/UploadImage";
import useNotify from "../unit/useNotify";
import YesNoInput3 from "../unit/YesNoInput3";
import AccessEdit from "./BankEditGroup/AccessEdit";
import ContactEdit from "./BankEditGroup/ContactEdit";
import EsignEdit from "./BankEditGroup/EsignEdit";
import LanguageEdit from "./BankEditGroup/LanguageEdit";
import LogoEdit from "./BankEditGroup/LogoEdit";
import ProfileEdit from "./BankEditGroup/ProfileEdit";
import WelcomeEdit from "./BankEditGroup/WelcomeEdit";
import AlertsEdit from "./BankEditGroup/AlertsEdit";

const MUI_X_PRODUCTS: any[] = [
  {
    id: "linkto_profile", //done
    label: "Profile",
    children: [],
  },
  {
    id: "linkto_welcome",
    label: "Welcome Page",
    children: [],
  },
  {
    id: "linkto_language",
    label: "Language Settings",
    children: [],
  },
  {
    id: "linkto_contact",
    label: "Customer Contact",
    children: [],
  },
  {
    id: "linkto_esign",
    label: "E-Signature",
    children: [],
  },
  {
    id: "linkto_logos",
    label: "Logos",
    children: [],
  },

  {
    id: "linkto_probation",
    label: "Access",
    children: [],
  },
  // { id: "linkto_alerts", label: "Alerts", children: [] },
];
const ShowEsignChangeEmailMessage = () => {
  const record = useRecordContext();
  if (record?.sNewEmail && record?.sNewEmail != record?.esign_email) {
    return (
      <Typography variant="caption" color="error">
        Email has been changed to{" "}
        <span style={{ fontWeight: "bold" }}>{record?.sNewEmail}</span>, waiting
        for confirmation.
      </Typography>
    );
  }
};

const CustomLinkField = ({ ...props }) => {
  const record = useRecordContext();
  const [link, setLink] = React.useState(record?.[props?.source] || "");
  const { success, error } = useNotify();

  const handleCopy = () => {
    navigator.clipboard.writeText(link);
    success("Link copied to clipboard");
  };
  return (
    <TextInput
      source={props?.source}
      label={props?.label}
      name={props?.name}
      variant="outlined"
      value={link}
      sx={{
        width: "100%",
        "& input.Mui-readOnly": {
          backgroundColor: "#ddd",
          borderRadius: "10px",
        },
      }}
      InputProps={{
        readOnly: true,
        endAdornment: (
          <InputAdornment position="end">
            <IconButton
              aria-label={"Copy URL"}
              onClick={handleCopy}
              edge="end"
              sx={{ marginLeft: "0px" }}
            >
              <CopyAllOutlined />
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );
};

const BankEdit = ({ ...props }) => {
  const record = useRecordContext();
  const [isEdit, setIsEdit] = React.useState(false);
  const [editItems, setEditItems] = React.useState([]);
  const [editEsignEmail, setEditEsignEmail] = React.useState(false);
  const { success, error } = useNotify();
  const id = record?.id;
  const [activeTab, setActiveTab] = React.useState(0);
  const handleChange = (newValue: number) => {
    setActiveTab(newValue);
  };
  const form = useFormContext();

  const authrole = localStorage.getItem("authrole");
  const [rightTabs, setRightTabs] = React.useState<any[]>([]);
  const [selectedItem, setSelectedItem] = React.useState("linkto_profile");
  const [selectSubItem, setSelectSubItem] = React.useState("linkto_profile");
  const scrollToSection = (sectionId: string) => {
    setSelectedItem(sectionId);
    setActiveTab(0);
    if (sectionId != selectedItem) {
      setIsEdit(false);
    }
  };
  const CustomLanguageSettings = () => {
    const record = useRecordContext();

    return (
      <CheckboxGroupInput
        source="sLanguage"
        label="Language Settings"
        defaultValue={record?.sLanguage || ["en"]}
        readOnly={isEdit ? false : true}
        row={false}
        fullWidth
        validate={[
          (value) => {
            if (value?.includes("en")) {
              return undefined;
            }
            return "English is required";
          },
        ]}
        choices={[
          { id: "en", name: "English", value: "en", required: true },
          { id: "es", name: "Spanish", value: "es" },
          { id: "ps", name: "Pashto", value: "ps" },
        ]}
        variant="outlined"
      />
    );
  };
  const ShowLogos = ({ ...props }) => {
    const size = props?.size || "large";
    const record = useRecordContext();
    const refresh = useRefresh();
    const [filelist, setFilelist] = React.useState<any[]>([]);
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "10px",
          mb: "20px",
        }}
      >
        {size == "small" && (
          <Typography variant="subtitle1">
            <b>Square logo</b> (as brand image shown on the top banner of
            customer site. Tip: Use logo for Twitter account)
          </Typography>
        )}
        {size != "small" && (
          <Typography variant="subtitle1">
            <b>Rectangular logo</b> (with institution name for Welcome Page and
            legal documents. Tip: Use full logo)
          </Typography>
        )}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: "10px",
            mb: "20px",
            "& .RaImageField-image": {
              height: "100px !important",
              width: "auto !important",
              objectFit: "contain",
            },
            "& > div": {
              height: "80px",
              display: "flex",
              alignItems: "center",
            },
          }}
        >
          {size == "small" ? (
            <>
              <Box sx={{ width: "100%", mt: 1 }}>
                {record?.sBankBcLogo && (
                  <ImageField
                    source="sBankBcLogo"
                    title="Logo"
                    sx={{
                      "& img": {
                        maxWidth: "100%",
                      },
                    }}
                  />
                )}
              </Box>
              <Box sx={{ width: "250px", mt: 1 }}>
                <UploadImage
                  uploadName="sBankBcLogo"
                  uploadApi={"banks/actions/uploadLogo/" + record?.id}
                  onSuccess={(filelist: any) => {
                    refresh();
                  }}
                  disabled={isEdit ? false : true}
                />
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ width: "100%", mt: 1 }}>
                {record?.sBankLogo && (
                  <ImageField
                    source="sBankLogo"
                    title="Logo"
                    sx={{
                      "& img": {
                        maxWidth: "100%",
                      },
                    }}
                  />
                )}
              </Box>
              <Box sx={{ width: "150px", mt: 1 }}>
                <UploadImage
                  uploadName="sBankLogo"
                  uploadApi={"banks/actions/uploadLogo/" + record?.id}
                  onSuccess={(filelist: any) => {
                    refresh();
                  }}
                  disabled={isEdit ? false : true}
                />
              </Box>
            </>
          )}
        </Box>
      </Box>
    );
  };

  const CustomAddress = ({ ...props }) => {
    const { record } = props;
    return (
      <Grid
        container
        spacing={2}
        sx={{
          mt: "0px",
          pt: "0px",
          "& .MuiGrid-item": { mt: "0px", pt: "0px" },
        }}
      >
        <Grid item xs={12} sm={6} sx={{ mt: "0px", pt: "0px" }}>
          <HmTextInput
            source="sStreet"
            name="street"
            label="Street"
            validate={[required()]}
          />
        </Grid>
        <Grid item xs={12} sm={6} sx={{ mt: "0px", pt: "0px" }}>
          <HmTextInput source="sStreet2" name="street2" label="Street 2" />
        </Grid>
        <Grid item xs={12} sm={6} sx={{ mt: "0px", pt: "0px" }}>
          <HmTextInput
            source="sCity"
            name="city"
            label="City"
            validate={[required()]}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <HmTextInput
            source="sState"
            name="state"
            label="State"
            validate={[required()]}
          />
        </Grid>
        <Grid item xs={12} sm={3}>
          <HmTextInput
            source="sZip"
            name="zip_code"
            label="Zip Code"
            validate={[required()]}
            className="numericText"
          />
        </Grid>
      </Grid>
    );
  };

  const handleChangeMenu = (itemId: string) => {
    let defaultSubItem = "linkto_english";
    if (itemId == "Description") {
      defaultSubItem = "linkto_english";
    } else if (itemId == "Attributes") {
      defaultSubItem = "linkto_loanterms";
    } else if (itemId == "Application") {
      defaultSubItem = "linkto_community";
    } else if (itemId == "Review") {
      defaultSubItem = "linkto_process";
    } else if (itemId == "Servicing") {
      defaultSubItem = "linkto_servicing_disbursement";
    } else if (itemId == "Legal & Compliance") {
      defaultSubItem = "linkto_authorization_application";
    } else if (itemId == "Marketing") {
      defaultSubItem = "linkto_landing";
    }
    setSelectSubItem(defaultSubItem);
    setActiveTab(0);
  };
  const admin = localStorage.getItem("authrole") == "admin" ? true : false;
  type BankEditFormFields = {
    institution_name?: string;
    organization_type?: string;
    business_type?: string;
    bussiness_name?: string;
    ein?: string;
    street?: string;
    street2?: string;
    city?: string;
    state?: string;
    zip_code?: string;
    bankUrl?: string;
    bank_phone?: string;
    bank_email?: string;
    contact_name?: string;
    textMessageNumber?: string;
    textMessageInstruction?: string;
    esign_firstname?: string;
    esign_lastname?: string;
    esign_email?: string;
    bankSolgan?: string;
    welcomeMsg?: string;
    language_settings?: string[];
  };
  const SaveFormButton = ({ ...props }) => {
    const label = props?.label || "Save";
    const id = form?.getValues("id");
    const { isDirty } = form.formState;
    const saveData = () => {
      const formData = form.getValues();

      let saveFormData = {};
      if (selectedItem === "linkto_profile") {
        saveFormData = {
          institution_name: formData.sBankName,
          organization_type: formData.sOrganizationType,
          business_type: formData.sBussinessType,
          business_name: formData.sBussinessClassification,
          ein: formData.sEin,
          street: formData.sStreet,
          street2: formData.sStreet2,
          city: formData.sCity,
          state: formData.sState,
          zip: formData.sZip,
          bankUrl: formData.sUrl,
          saveType: "info",
        };
        if (!saveFormData?.organization_type) {
          error("Organization Type is required");
          return;
        }
        if (!saveFormData?.business_type) {
          error("Business Type is required");
          return;
        }
        if (!saveFormData?.business_name) {
          error("Business Name is required");
          return;
        }
        if (!saveFormData?.ein) {
          error("EIN is required");
          return;
        }
        if (!saveFormData?.street) {
          error("Street is required");
          return;
        }
        if (!saveFormData?.city) {
          error("City is required");
          return;
        }
        if (!saveFormData?.state) {
          error("State is required");
          return;
        }
        if (!saveFormData?.zip) {
          error("Zip Code is required");
          return;
        }
        if (!saveFormData?.bankUrl) {
          error("Institution Website is required");
          return;
        }
      } else if (selectedItem === "linkto_contact") {
        saveFormData = {
          phone: formData.sBankPhone,
          bank_email: formData.sBankSupport,
          contactName: formData.sBankContactName,
          textMessageNumber: formData.textMessageNumber,
          textMessageInstruction: formData.textMessageInstruction,
          saveType: "contact",
        };
        if (!saveFormData?.phone) {
          error("Contact Phone Number is required");
          return;
        }
        if (!saveFormData?.bank_email) {
          error("Contact Email Address is required");
          return;
        }
        if (!saveFormData?.contactName) {
          error("Contact Name is required");
          return;
        }
      } else if (selectedItem === "linkto_esign") {
        saveFormData = {
          esign_firstname: formData.esign_firstname,
          esign_lastname: formData.esign_lastname,
          esign_email: formData.esign_email,
          saveType: "esign",
        };
        if (!saveFormData?.esign_firstname) {
          error("E-Signature Sender First Name is required");
          return;
        }
        if (!saveFormData?.esign_lastname) {
          error("E-Signature Sender Last Name is required");
          return;
        }
        if (!saveFormData?.esign_email) {
          error("E-Signature Sender Email is required");
          return;
        }
      } else if (selectedItem === "linkto_welcome") {
        saveFormData = {
          bankSolgan: formData.sSlogan,
          welcomeMsg: formData.sWelcomeMsg,
          saveType: "welcome",
        };
        if (!saveFormData?.bankSolgan) {
          error("Welcome Headline is required");
          return;
        }
        if (!saveFormData?.welcomeMsg) {
          error("Welcome Message is required");
          return;
        }
      } else if (selectedItem === "linkto_logos") {
        if (!formData.sBankBcLogo) {
          error("Small logo is required");
          return;
        }
        if (!formData.sBankBcLogo) {
          error("Large logo is required");
          return;
        }
        saveFormData = {
          sBankBcLogo: formData.sBankBcLogo,
          sBankLogo: formData.sBankLogo,
          saveType: "logos",
        };
      } else if (selectedItem === "linkto_language") {
        if (!formData.sLanguage || !formData.sLanguage.includes("en")) {
          error("English is required in Language Settings");
          return;
        }
        saveFormData = {
          language_settings: formData.sLanguage,
          saveType: "language",
        };
      } else if (selectedItem === "linkto_probation") {
        saveFormData = {
          iProbation: formData.iProbation,
          dProbationStartTime: formData.dProbationStartTime,
          dProbationEndTime: formData.dProbationEndTime,
          sAccess: formData.sAccess,
          saveType: "probation",
        };
      } else {
        saveFormData = {};
      }

      console.log("saveFormData:", saveFormData);
      const reqUrl = "banks/actions/edit";
      if (saveFormData) {
        sendRequest(reqUrl, { id: id, data: saveFormData }).then((res) => {
          if (res?.code == 200) {
            success("Saved successfully");
          } else {
            error("Error saving " + res?.message);
          }
        });
      } else {
        error("Error saving description: No data to save");
      }
    };
    if (selectedItem == "linkto_logos") {
      return null;
    }
    return (
      <>
        <Button
          variant="contained"
          color="primary"
          onClick={saveData}
          disabled={!isDirty}
          sx={{
            width: "120px",
          }}
        >
          {label}
        </Button>
      </>
    );
  };
  const CustomToolbar = (props: any) => {
    if (selectedItem == "linkto_probation" || selectedItem == "linkto_logos") {
      return null;
    }
    if (isEdit == false) {
      return (
        <Toolbar
          {...props}
          sx={{ display: "flex", justifyContent: "right", background: "#fff" }}
        >
          <div>
            <Button
              variant="contained"
              onClick={(e) => setIsEdit(true)}
              sx={{
                width: "120px",
              }}
            >
              Edit
            </Button>
          </div>
        </Toolbar>
      );
    }
    return (
      <Toolbar
        {...props}
        sx={{ display: "flex", justifyContent: "right", background: "#fff" }}
      >
        <div>
          <SaveFormButton label="Save" />
        </div>
      </Toolbar>
    );
  };

  const handleChangeEsignEmail = () => {};
  const sendRequestToAdmin = (id: any, type: string) => {
    const reqUrl = "banks/actions/requestFullAccess";
    var response = sendRequest(reqUrl, {
      id: id,
      data: {
        type: type,
      },
    });
    response.then((res) => {
      if (res?.code == 200) {
        success("Request sent to admin successfully");
      } else {
        error("Error sending request to admin: " + res?.message);
      }
    });
  };
  const ProbationDateField = ({ ...props }) => {
    const record = useRecordContext();
    const [setProbation, setSetProbation] = React.useState(
      record?.iProbation == 1 ? 1 : 0
    );
    if (admin) {
      return (
        <Grid
          container
          spacing={2}
          sx={{
            mt: "0px",
            pt: "0px",
            "& .MuiGrid-item": { mt: "0px", pt: "0px" },
          }}
        >
          {admin && (
            <Grid item xs={12} sm={12}>
              <YesNoInput3
                source="iProbation"
                label="Is Probation?"
                defaultValue={record?.iProbation == 1 ? true : false}
                onChange={(e) => {
                  setSetProbation(e.target.value);
                }}
                readOnly={admin ? (isEdit ? false : true) : false}
              />
            </Grid>
          )}

          {admin && setProbation == 1 && (
            <>
              <Grid item xs={12} sm={6}>
                <DateInput
                  source="dProbationStartTime"
                  label="Probation Start Date"
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <DateInput
                  source="dProbationEndTime"
                  label="Probation End Date"
                  variant="outlined"
                  onChange={(event: any) => {
                    const value = event.target.value;
                    if (value) {
                      const startDate = new Date(
                        document.querySelector(
                          'input[name="dProbationStartTime"]'
                        )?.value
                      );
                      const endDate = new Date(value);
                      if (endDate < startDate) {
                        error("Probation End Date cannot be before Start Date");
                      }
                    }
                  }}
                />
              </Grid>
            </>
          )}
          {admin && (
            <Grid item xs={12} sm={12}>
              <SelectInput
                source="sAccess"
                label="Access"
                choices={[
                  { id: "business", name: "Business Products" },
                  { id: "personal", name: "Consumer Products" },
                  { id: "full", name: "Full Access" },
                ]}
                sx={{
                  "& .MuiFormLabel-root span span": { display: "none" },
                }}
                variant="outlined"
                fullWidth
                validate={[required()]}
                readOnly={admin ? (isEdit ? false : true) : false}
              />
            </Grid>
          )}
        </Grid>
      );
    } else {
      if (record?.iProbation == 1) {
        return (
          <Grid container sx={{ mt: "10px" }}>
            <HeaderLabelGridField
              title="Your institution is currently under probation"
              value=""
              dxs={1}
              tdxs={11}
            />
            <HeaderLabelGridField
              title="Start date"
              value={
                record?.dProbationStartTime
                  ? format(new Date(record?.dProbationStartTime), "MM/dd/yyyy")
                  : ""
              }
              dxs={10}
              tdxs={2}
            />
            <HeaderLabelGridField
              title="End date"
              value={
                record?.dProbationEndTime
                  ? format(new Date(record?.dProbationEndTime), "MM/dd/yyyy")
                  : ""
              }
              dxs={10}
              tdxs={2}
            />
            <Typography sx={{ mt: 2 }}>
              Click{" "}
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  sendRequestToAdmin(record?.id, "create");
                }}
                to={""}
              >
                here
              </Link>{" "}
              to request full access.
            </Typography>
          </Grid>
        );
      } else {
        const accessBussiness =
          record?.sAccess == "business" || record?.sAccess == "full"
            ? true
            : false;
        const accessPersonal =
          record?.sAccess == "personal" || record?.sAccess == "full";
        return (
          <>
            This institution has access to:
            <>
              <Typography sx={{ mt: 2 }}>
                {record?.sAccess}--
                {accessBussiness ? (
                  <TaskAltIcon
                    sx={{
                      verticalAlign: "top",
                      fontSize: "18px",
                      mt: 0.1,
                      mr: 0.5,
                    }}
                  />
                ) : (
                  <RadioButtonUncheckedIcon
                    sx={{
                      verticalAlign: "top",
                      fontSize: "18px",
                      mt: 0.1,
                      mr: 0.5,
                    }}
                  />
                )}
                Business products
              </Typography>
            </>
            <>
              <Typography>
                {accessPersonal ? (
                  <TaskAltIcon
                    sx={{
                      verticalAlign: "top",
                      fontSize: "18px",
                      mt: 0.1,
                      mr: 0.5,
                    }}
                  />
                ) : (
                  <RadioButtonUncheckedIcon
                    sx={{
                      verticalAlign: "top",
                      fontSize: "18px",
                      mt: 0.1,
                      mr: 0.5,
                    }}
                  />
                )}
                Consumer products
              </Typography>
            </>
            {/* {record?.sAccess == "full" && (
              <Typography sx={{ fontWeight: "bold" }}>
                Consumer and Business products
              </Typography>
            )} */}
            <Typography sx={{ mt: 2 }}>
              Click{" "}
              <Link
                onClick={(e) => {
                  e.preventDefault();
                  sendRequestToAdmin(record?.id, "change");
                }}
                to={""}
              >
                here
              </Link>{" "}
              to request change of access.
            </Typography>
          </>
        );
      }
    }
  };

  return (
    <>
      <div className="edit-container">
        <div className="aside-left">
          <BankListAside selected="banks" />
        </div>
        <div
          className="main-content"
          style={{
            order: 2,
            flexGrow: 1,
            marginTop: "65px",
            position: "relative",
          }}
        >
          <Box sx={{ borderBottom: 1, borderColor: "#00ccb1" }}>
            {MUI_X_PRODUCTS.map((item, index) => {
              if (item.id == selectedItem) {
                return (
                  <CustomTabButton
                    key={item.id}
                    variant="text"
                    className={"Mui-selected"}
                    sx={{ minWidth: "150px", width: "150px" }}
                  >
                    {item.label}
                    {record?.iProbation == 1 &&
                      item.id == "linkto_probation" && (
                        <span style={{ color: "red", marginLeft: "10px" }}>
                          *
                        </span>
                      )}
                  </CustomTabButton>
                );
              } else {
                return (
                  <CustomTabButton
                    key={item.id}
                    variant="text"
                    sx={{ minWidth: "140px", width: "140px" }}
                    onClick={() => {
                      scrollToSection(item.id);
                    }}
                  >
                    {item.label}
                    {record?.iProbation == 1 &&
                      item.id == "linkto_probation" && (
                        <span style={{ color: "red", marginLeft: "10px" }}>
                          *
                        </span>
                      )}
                  </CustomTabButton>
                );
              }
            })}
            {admin && (
              <Link to="/banks">
                <Tooltip title="Return to Institutions List" placement="left">
                  <ArrowCircleLeftIcon
                    color="primary"
                    sx={{ float: "right", mt: 1, mr: 1 }}
                  />
                </Tooltip>
              </Link>
            )}
          </Box>
          <Box
            sx={{
              mb: 0,
              mt: "10px",
              height: `calc(100vh - 237px)`,
              width: `calc(100vw - 409px)`,
              maxWidth: `calc(100vw - 409px)`,
              borderRadius: "0px",
              overflowY: "auto",
              background: "#fff",
              border: "1px solid #e0e0e3",
              "& .MuiCard-root": {
                border: "0px",
                borderRadius: "0px",
                background: "#fff",
              },
              "& .MuiCardContent-root": {
                background: "#fff",
              },
            }}
          >
            {/* <SimpleForm toolbar={<CustomToolbar isEdit={isEdit} />}> */}
            <SectionStyled
              id={"section_bankedit"}
              sx={{
                width: "100%",
              }}
            >
              <Box
                sx={{
                  padding: "10px",
                  paddingTop: "0px",
                  paddingBottom: "0px",
                }}
              >
                {/* hidden button */}
                <input type="hidden" name="saveAction" value={selectedItem} />
                {/* base info =>done*/}
                {selectedItem == "linkto_profile" && <ProfileEdit />}
                {/* contact ==>done*/}
                {selectedItem == "linkto_contact" && <ContactEdit />}
                {/* E-Signature Sender=>done */}
                {selectedItem == "linkto_esign" && <EsignEdit />}
                {/* Logos ==>done*/}
                {selectedItem == "linkto_logos" && <LogoEdit />}
                {/* Welcome Page=>done */}
                {selectedItem == "linkto_welcome" && <WelcomeEdit />}
                {/* Language Settings =>done */}
                {selectedItem == "linkto_language" && <LanguageEdit />}
                {/* Probation ==>done */}
                {selectedItem == "linkto_probation" && <AccessEdit />}
                {/* Alerts*/}
                {selectedItem == "linkto_alerts" && <AlertsEdit />}
              </Box>
            </SectionStyled>
            {/* </SimpleForm> */}
          </Box>
        </div>
      </div>
    </>
  );
};

export default BankEdit;
